// =============================================================================
//          (c) Copyright 2021 by Brinks Home Security | Dallas, TX, USA
// =============================================================================
// Version   : 0.1.1
// Created at: 08.24.16
// Project   : Brinks Home Security Shared Assets
// -----------------------------------------------------------------------------
// Use ScrollFade to show/hide an element at a certain scroll target.
// - Set `data-fade-in`/`data-fade-out` to the id of the target element (where
//   the hide/show should occur).
// - Responsive-friendly: add `data-scroll-fade-media` to the element and set
//   it to a specific breakpoint (i.e. 'sm') or breakpoint range (i.e. 'sm-down').
//   Simpy leave this data-attribute off to reveal at all screen sizes.
// - For fade-in elements, add a `hidden-*` utility class to ensure that the
//   element is hidden on load at the correct breakpoints.
// -----------------------------------------------------------------------------

import Breakpoints from './breakpoints'

const ScrollFade = (($) => {
  const Selectors = {
    FADE_ANY: '[data-fade-in], [data-fade-out]',
    SHOW: 'show'
  }

  const DataKeys = {
    FADE_IN: 'fade-in',
    FADE_OUT: 'fade-out',
    BREAKPT: 'scroll-fade-media'
  }

  /* --------------------------------------------------------------------------
    Private
  -------------------------------------------------------------------------- */

  function initScrollListener ($el) {
    const dataFadeIn = $el.data(DataKeys.FADE_IN)
    const dataFadeOut = $el.data(DataKeys.FADE_OUT)
    const target = dataFadeIn || dataFadeOut

    let shownEvent = $.Event(ScrollFade.Event.SHOWN)
    let hiddenEvent = $.Event(ScrollFade.Event.HIDDEN)

    let fadeCondition

    // Necessary setup of fade-in elements
    // Add 'fade' & remove any 'hidden-*' classes, needed to prevent FOUC
    if (dataFadeIn) {
      $el.addClass('fade')
      $el.hide()
    }

    $(window).on(ScrollFade.Event.SCROLL, () => {
      let targetTop

      if ($(target).length) {
        targetTop = $(target).offset().top
      } else {
        return
      }

      if (dataFadeIn) {
        fadeCondition = $(window).scrollTop() > targetTop
      } else if (dataFadeOut) {
        fadeCondition = $(window).scrollTop() < targetTop
      }

      // Make use of Bootstraps `.fade.show` classes to animate fading in and out
      if (fadeCondition) {
        // Reveal only if NOT already showing
        if (!$el.hasClass(Selectors.SHOW)) {
          $el.fadeIn(150)
          $el.addClass(Selectors.SHOW) // Animates opacity to 1
          $el.trigger(shownEvent)
        }
        // Hide only if already showing
      } else if ($el.hasClass(Selectors.SHOW)) {
        $el.hide()
        $el.removeClass(Selectors.SHOW) // Animates opacity to 0
        $el.trigger(hiddenEvent)
      }
    })
  }

  /* --------------------------------------------------------------------------
    Public
  -------------------------------------------------------------------------- */

  let ScrollFade = {
    // Make events public since they will be broadcast
    Event: {
      SHOWN: 'shown.scrollFade',
      HIDDEN: 'hidden.scrollFade',
      SCROLL: 'scroll.scrollFade'
    },

    init () {
      for (let el of $(Selectors.FADE_ANY)) {
        let $el = $(el)
        let breakpoint = $el.data(DataKeys.BREAKPT)

        // Only run if matches breakpoint or no breakpoint specified
        if (Breakpoints.is(breakpoint) || !breakpoint) {
          initScrollListener($el)
        }
      }
    },

    deactivate () {
      $(window).off(this.Event.SCROLL)
    }
  }

  return ScrollFade
})(jQuery)

export default ScrollFade
